import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";

const TranslationContext = createContext();

export function TranslationProvider({ children, initialLocale = "hu", initialTranslations = {} }) {
  const [locale, setLocale] = useState(initialLocale);
  const [translations, setTranslations] = useState(initialTranslations);
  const [loading, setLoading] = useState(false);

  // opcionális: cache-eljük locale szerint, hogy ne fetch-eljen feleslegesen
  const cacheRef = useRef({ [initialLocale]: initialTranslations });

  useEffect(() => {
    let cancelled = false;

    async function loadTranslations() {
      // ✅ ha van cache, azonnal használjuk (0 villanás)
      const cached = cacheRef.current[locale];
      if (cached && Object.keys(cached).length > 0) {
        setTranslations(cached);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(`/api/translations/fast?locale=${encodeURIComponent(locale)}`);
        const json = await res.json();
        const map = json?.data?.translations || {};

        if (!cancelled) {
          cacheRef.current[locale] = map;
          setTranslations(map);
        }
      } catch (err) {
        console.error("Translation load error:", err);
        if (!cancelled) setTranslations({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTranslations();
    return () => { cancelled = true; };
  }, [locale]);

  // ✅ t(): ha nincs meg a fordítás, ne villogjon "key"-vel
  // FONTOS: nálad sok helyen t(product.name) / t(variant.name) lehet "nem kulcs" (valódi szöveg).
  // Ezért: ha dotted key (pl. product.add.to.cart), akkor üres string, különben visszaadjuk az inputot.
  const t = useCallback((key) => {
    const v = translations[key];
    if (v !== undefined && v !== null) return v; // üres string is ok

    // ✅ fallback: ha fordítási kulcs (dotted), jelenjen meg a kulcs
    if (typeof key === "string" && key.includes(".")) return key;

    // nem kulcs jellegű stringek (pl. terméknév) maradhatnak
    return key ?? "";
  }, [translations]);

  return (
    <TranslationContext.Provider value={{ t, locale, setLocale, loading, rawTranslations: translations }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  return useContext(TranslationContext);
}
